import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  EstadosAgendamientoEnumChoice,
  EstadosContratoChoiceEnum,
  ModulosEnumChoice,
  ModulosMotivoENUM,
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  TipoParametroChoiceEnum,
} from '@/shared';
import { useFetchAgendamientos, useFetchMotivos } from '@/shared/actions/erp';
import { GeneralTable, TableTabsScene } from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import {
  Agendamiento,
  AlertSeverityEnum,
  EstadoBitacoraEnum,
  EstadoMotivoEnum,
  IDataBitacoraRespById,
  Venta,
} from '@/shared/interfaces';
import { useColumnsAgendamientos } from '../../shared/hooks';
import { Grid, IconButton, Tooltip } from '@mui/material';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { Edit } from '@mui/icons-material';
import { useDiccionarioMotivo } from '@/store/erp/diccionario_motivo/diccionario_motivo.store';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';

export type AgendamientosAprobadosPageProps = {
  stateAgendamiento?: EstadosAgendamientoEnumChoice;
};

const AgendamientosAprobadosPage: React.FC<AgendamientosAprobadosPageProps> = ({
  stateAgendamiento,
}) => {
  const navigate = useNavigate();
  useCheckPermissions(PermissionsEnum.api_view_agendamiento);

  const returnUrlAgendamientosPyLPage = ROUTER_PATHS.operaciones.agendamientoPyLNav;
  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  const {
    setOpenCreaBitacora,
    setDetalle,
    setFaseVenta,
    setDataGetVentaById,
    setOpen,
    socket,
    departamento: depSocket,
  } = useDiccionarioMotivo();

  const user = useAuthStore(s => s.user);
  const setAlert = useUiStore(s => s.setAlert);

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: agendamientosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchAgendamientos({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      identificacion: searchTerm,
      ...filterObject,
      filterByState: false,
      // Tab "Agendadas" debe mostrar tanto los historicos FINALIZADO (instalaciones
      // ya completadas, comportamiento original de prod) como los nuevos APROBADO
      // creados por el flujo "reportar novedad -> Recordar" del tecnico.
      estado_agendamiento_in: stateAgendamiento
        ? stateAgendamiento
        : `${EstadosAgendamientoEnumChoice.APROBADO},${EstadosAgendamientoEnumChoice.FINALIZADO}`,
      contrato_estado_contrato_exclude: EstadosContratoChoiceEnum.ACTIVO,
      tipo_orden_servicio_exclude: 'TRASLADO',
    },
  });

  ///* fetch motivos de bitacora para auto-creacion cuando el registro viene
  ///  desde un "reportar novedad" del tecnico (tipo_novedad presente).
  const { data: dataMotivos } = useFetchMotivos({
    estado: true,
    modulo_in: ModulosMotivoENUM.BITACORA_PYL,
    page_size: 256,
  });

  ///* columns
  const { agendamientosAprobados, agendamientosRecoordinados } =
    useColumnsAgendamientos();

  const onEdit = (agendamiento: Agendamiento) => {
    navigate(
      `${returnUrlAgendamientosPyLPage}/reasignar/${agendamiento.id_agendamiento}`
    );
  };

  return (
    <TableTabsScene
      globalFilter={globalFilter}
      onChangeFilter={onChangeFilter}
      searchInputText="por identificación"
      //onChangeRangeDate={onChangeRangeDate}
      cleanFilter={cleanFilter}
    >
      <GeneralTable<Agendamiento>
        columns={
          // Default (sin prop) y APROBADO/FINALIZADO usan las columnas de
          // "Agendadas"; RECORDINADO usa las columnas de recordinacion.
          stateAgendamiento === EstadosAgendamientoEnumChoice.RECORDINADO
            ? agendamientosRecoordinados
            : agendamientosAprobados
        }
        paintRow={(row)=>{
          if(
            row?.estado_llamada === "NO REALIZADA" 
            && JSON.stringify(row?.observaciones_x)?.includes("RE_COORDINAR") 
          ){
            return "#FFFA94"  
          }
          return ""
        }}
        data={agendamientosPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={agendamientosPagingRes?.data?.count}
        // // // actions ========
        enableColumnActions={true}
        enableEditing={true}
        canEdit={false}
        // crud
        showCustomActionBtnSpace={true}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.COLUMN_WIDTH_SMALL}
        customActionBtnSpace={(row) => {

          return (
            <Grid container>
              <Grid item>
                <Tooltip title='Bitácora'>
                  <IconButton
                    onClick={() => {
                      console.log('[BITACORA-AGENDADAS] Click button. observaciones_x:', row.observaciones_x);
                      if (!socket) {
                        console.warn('[BITACORA-AGENDADAS] socket is null');
                        setAlert(true, 'Conexión de bitácora no disponible. Recarga la página.', AlertSeverityEnum.error);
                        return;
                      }

                      const idPreagendamiento = row.preagendamiento_data?.id_preagendamiento;
                      console.log('[BITACORA-AGENDADAS] idPreagendamiento:', idPreagendamiento);
                      if (!idPreagendamiento) {
                        console.warn('[BITACORA-AGENDADAS] No idPreagendamiento');
                        setAlert(true, 'No se encontró el preagendamiento de esta fila.', AlertSeverityEnum.error);
                        return;
                      }

                      // Datos del rechazo/recoordinacion que el tecnico reporto
                      // (los guarda el backend en observaciones_x cuando entra al
                      // bloque de recoordination_required=true en
                      // OrdenTrabajoDetailView.metodo_extencion_put).
                      const observacionTecnico = row.observaciones_x?.observaciones?.observacion_rechazo_tecnico || '';
                      const motivoRechazoTecnico = row.observaciones_x?.motivo_rechazo || '';
                      const tipoNovedad = row.observaciones_x?.tipo_novedad || '';
                      const detalleFallback = {
                        numero_referencia_venta: idPreagendamiento,
                        identificacion_cliente: row.prospecto_data?.identificacion,
                        nombre_cliente: row.prospecto_data?.razon_social,
                        canal_venta: row.prospecto_data?.canal_venta_create,
                        vendedor: row.prospecto_data?.user_create,
                        fecha_aceptacion: row.preventa_data?.fecha_aceptacion,
                        // Campos extra para el pre-fill del BitacoraDialog
                        observacion_rechazo_tecnico: observacionTecnico,
                        motivo_rechazo_tecnico: motivoRechazoTecnico,
                        tipo_novedad: tipoNovedad,
                        estado_agendamiento: row.estado_agendamiento,
                      };

                      const abrirCreacionManual = () => {
                        setDetalle(detalleFallback);
                        setFaseVenta(ModulosEnumChoice.RECORDINADO);
                        setOpenCreaBitacora(true);
                      };

                      // Indicador confiable de origen tecnico: el backend
                      // solo setea observaciones_x.tipo_novedad cuando el tecnico
                      // reporta una novedad. Otros origenes no lo tienen.
                      const esOriginadoPorTecnico = !!tipoNovedad;
                      console.log('[BITACORA-AGENDADAS] tipoNovedad:', tipoNovedad, '| esOriginadoPorTecnico:', esOriginadoPorTecnico);

                      console.log('[BITACORA-AGENDADAS] Emitting getVentaById...');
                      socket.emit('getVentaById', ({
                        numero_referencia_venta: idPreagendamiento,
                      }), (data: IDataBitacoraRespById) => {
                        console.log('[BITACORA-AGENDADAS] getVentaById response:', data);
                        if (data?.code == 200 && data.data) {
                          // Bitacora ya existe -> poblar store y abrir lectura
                          // (mismo patron que AgendasPendingRecoordinacionPyLPage).
                          console.log('[BITACORA-AGENDADAS] Bitacora exists, setting data and opening read view');
                          setDataGetVentaById(data.data);
                          setOpen(true);
                          return;
                        }

                        // Bitacora no existe -> decidir flujo segun origen
                        if (!esOriginadoPorTecnico) {
                          // Origen NO tecnico -> abrir modal manual (comportamiento prod)
                          console.log('[BITACORA-AGENDADAS] Bitacora does NOT exist and origen NOT tecnico -> opening MANUAL modal');
                          abrirCreacionManual();
                          return;
                        }
                        console.log('[BITACORA-AGENDADAS] Bitacora does NOT exist, origen IS tecnico -> AUTO-CREATING');

                        // Origen tecnico -> intentar auto-crear con datos del rechazo.
                        // Mismo patron que AgendasPendingRecoordinacionPyLPage:
                        //   1) Buscar el motivo (codigo + modulo === depSocket).
                        //   2) createVenta con el comentario en texto.
                        //   3) Adjuntar las imagenes del tecnico como comentarios tipo IMAGE.
                        const esRecoordinacion =
                          tipoNovedad === 'RE_COORDINAR' ||
                          row.estado_agendamiento === 'RECORDINADO';
                        const motivoCodigo = esRecoordinacion
                          ? 'RECORDINADO_PLANTA_EXTERNA_PYL'
                          : 'FALLIDO_PLANTA_EXTERNA_PYL';

                        const motivoEncontrado = dataMotivos?.data?.data?.find(
                          (m: any) => m.codigo === motivoCodigo && m.modulo === depSocket
                        );
                        console.log('[BITACORA-AGENDADAS] motivoCodigo:', motivoCodigo, '| depSocket:', depSocket, '| motivoEncontrado:', motivoEncontrado);

                        if (!motivoEncontrado) {
                          console.warn('[BITACORA-AGENDADAS] motivo no encontrado, fallback a modal manual');
                          abrirCreacionManual();
                          return;
                        }

                        const textoComentario = motivoRechazoTecnico
                          ? `Motivo: ${motivoRechazoTecnico} - ${observacionTecnico}`
                          : observacionTecnico;

                        // Info del tecnico que reporto (guardada por el backend
                        // en observaciones_x al crear el agendamiento APROBADO).
                        const tecnicoUsername = row.observaciones_x?.tecnico_username ?? '';
                        const tecnicoRol = row.observaciones_x?.tecnico_rol ?? '';
                        const tecnicoDepartamento = row.observaciones_x?.tecnico_departamento_nombre ?? 'TECNICO';

                        // URLs de las evidencias subidas a S3 por el tecnico
                        // (observaciones_x.urls_fotos_rechazo_tecnico).
                        const imageUrlsTecnico: string[] = (row.observaciones_x?.urls_fotos_rechazo_tecnico ?? [])
                          .map((f: any) => f?.photoUrl)
                          .filter((u: any): u is string => !!u);

                        const ventaData = {
                          numero_referencia_venta: idPreagendamiento,
                          identificacion_cliente: row.prospecto_data?.identificacion,
                          nombre_cliente: row.prospecto_data?.razon_social,
                          canal_venta: row.prospecto_data?.canal_venta_create,
                          vendedor: row.prospecto_data?.user_create,
                          fecha_aceptacion: row.preventa_data?.fecha_aceptacion,
                          creado_por: (tecnicoUsername || user?.username) ?? '',
                          fase_venta: ModulosEnumChoice.RECORDINADO,
                          motivos: [{
                            numero_referencia_motivo: motivoEncontrado.id_motivo,
                            descripcion: motivoEncontrado.nombre,
                            prioridad: motivoEncontrado.prioridad || 'ALTA',
                            estado_motivo: EstadoMotivoEnum.NO_RESUELTO,
                            modulo: motivoEncontrado.modulo,
                            comentarios: [{
                              comentario: textoComentario || 'Novedad reportada por técnico',
                              username: (tecnicoUsername || user?.username) ?? '',
                              rol: (tecnicoRol || user?.rol) ?? '',
                              departamento: tecnicoDepartamento,
                              tipo: TipoParametroChoiceEnum.TEXTO,
                            }]
                          }]
                        };

                        // Flujo original: crear nueva bitácora ligada a este preagendamiento.
                        const crearNuevaBitacora = () => {
                          console.log('[BITACORA-AGENDADAS] Emitting createVenta with:', ventaData);
                          socket.emit('createVenta', { ventaData }, (createResponse: IDataBitacoraRespById) => {
                            console.log('[BITACORA-AGENDADAS] createVenta response:', createResponse);
                            if (createResponse?.code != 200) {
                              setAlert(true, 'No se pudo auto-crear la bitácora. Abriendo formulario manual.', AlertSeverityEnum.warning);
                              abrirCreacionManual();
                              return;
                            }
                            setAlert(true, createResponse.message, AlertSeverityEnum.success);

                            setTimeout(() => {
                              socket.emit('getVentaById', {
                                numero_referencia_venta: idPreagendamiento,
                              }, (getResponse: IDataBitacoraRespById) => {
                                if (getResponse?.code != 200 || !getResponse.data) {
                                  setOpen(true);
                                  return;
                                }
                                const venta = getResponse.data;
                                const dm = venta.diccionario_motivo?.[0];
                                const motivo = dm?.motivos?.[0];

                                if (dm && motivo && imageUrlsTecnico.length > 0) {
                                  imageUrlsTecnico.forEach(url => {
                                    socket.emit('enviarComentario', {
                                      numero_referencia_venta: venta.numero_referencia_venta,
                                      diccionario_motivo_id: dm._id,
                                      motivo_id: motivo._id,
                                      comentario_texto: url,
                                      username_create_venta: (tecnicoUsername || user?.username) ?? '',
                                      tipo: TipoParametroChoiceEnum.IMAGE,
                                      departamento: tecnicoDepartamento,
                                      username: (tecnicoUsername || user?.username) ?? '',
                                      rol: (tecnicoRol || user?.rol) ?? '',
                                    });
                                  });
                                }

                                setDataGetVentaById(venta);
                                setOpen(true);
                              });
                            }, 200);
                          });
                        };

                        // Antes de crear, buscar bitácoras existentes del mismo cliente
                        // (por identificacion_cliente) para evitar duplicar el registro
                        // en /operaciones/bitacora. Si existe al menos una activa con un
                        // motivo NO_RESUELTO, agregamos el reporte del técnico como
                        // comentario cronológico a ese motivo.
                        const identificacionCliente = row.prospecto_data?.identificacion;

                        if (!identificacionCliente) {
                          crearNuevaBitacora();
                          return;
                        }

                        let resueltoFiltro = false;
                        const handlerFiltro = (response: {
                          code?: number;
                          data?: { data?: Venta[] };
                        }) => {
                          if (resueltoFiltro) return;
                          if (response?.code !== 200) return;
                          const ventasAll = Array.isArray(response?.data?.data)
                            ? response.data.data
                            : [];
                          const bitacorasCliente = ventasAll.filter(
                            v => v.identificacion_cliente === identificacionCliente,
                          );
                          resueltoFiltro = true;
                          socket.off('getVentas', handlerFiltro);

                          if (bitacorasCliente.length === 0) {
                            console.log('[BITACORA-AGENDADAS] No existe bitácora previa del cliente -> createVenta');
                            crearNuevaBitacora();
                            return;
                          }

                          // Solo lectura: el comentario del técnico ya lo emitió
                          // RejectInstalacionAsigTecModal al reportar novedad. Re-emitir
                          // aquí duplica el mensaje en la bitácora. Por eso solo abrimos
                          // la bitácora activa más reciente del cliente.
                          const bitacoraExistente = bitacorasCliente
                            .filter(v =>
                              v.estado_bitacora === EstadoBitacoraEnum.EN_PROCESO &&
                              v.diccionario_motivo?.some(dm =>
                                dm.motivos?.some(
                                  m => m.estado_motivo === EstadoMotivoEnum.NO_RESUELTO,
                                ),
                              ),
                            )
                            .sort(
                              (a, b) =>
                                new Date(b.updatedAt).getTime() -
                                new Date(a.updatedAt).getTime(),
                            )[0];

                          if (!bitacoraExistente) {
                            console.log('[BITACORA-AGENDADAS] Bitácoras del cliente sin motivo abierto -> createVenta');
                            crearNuevaBitacora();
                            return;
                          }

                          setDataGetVentaById(bitacoraExistente);
                          setOpen(true);
                        };

                        socket.on('getVentas', handlerFiltro);
                        console.log('[BITACORA-AGENDADAS] Buscando bitácora existente del cliente:', identificacionCliente);
                        socket.emit('getVentasFilter', {
                          page: 1,
                          pageSize: 100,
                          filtros: { identificacion_cliente: identificacionCliente },
                        });
                        // Red de seguridad: si chattest no responde en 3s, crear nueva.
                        setTimeout(() => {
                          if (!resueltoFiltro) {
                            resueltoFiltro = true;
                            socket.off('getVentas', handlerFiltro);
                            console.warn('[BITACORA-AGENDADAS] timeout getVentasFilter, fallback a createVenta');
                            crearNuevaBitacora();
                          }
                        }, 3000);
                      });
                    }}
                  >
                    <ForumRoundedIcon />
                  </IconButton>

                </Tooltip>
                <IconButton
                  onClick={() => onEdit(row)}
                >
                  <Edit />
                </IconButton>
              </Grid>
            </Grid>
          )
        }}


      />
    </TableTabsScene>
  );
};

export default AgendamientosAprobadosPage;
